import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState, FC } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useGetReservationFlowProduct } from 'src/hooks/api/reservation-flow/useGetReservationFlowProduct';
import {
  ReservationFlowInvalidRequestError,
  ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
} from 'src/interfaces/reservation_flow';
import { InputPaymentInfoTemplate } from 'src/templates/merchants/purchase/InputPaymentInfoTemplate';

import { injectAppContextGetServerSideProps } from 'src/pages/_app';
import { useMerchantDetail } from 'src/hooks/api/merchant';
import { useIntl } from 'react-intl';
import { fetchCsrfToken, fetchMerchantDetail } from 'src/infrastructure/api';
import {
  isAxiosError,
  isAPIError,
  useClientSideApiClient,
} from 'src/openapi/axiosClient';
import { isReservationFlowInvalidRequestError } from 'src/state-machines/booking/functions/reservation-flow-invalid-request/is-reservation-flow-invalid-request-error';
import { RSToastContainer } from 'src/reservation-page/toast/RSToastContainer';
import { toast } from 'src/reservation-page/toast';
import { filterReservationFlowInvalidRequestDetails } from 'src/state-machines/booking/functions/reservation-flow-invalid-request/filter-reservation-flow-invalid-request-details';
import MetaTag from 'src/components/layouts/MetaTag/MetaTag';
import { TicketBookConfirmTemplate } from 'src/templates/merchants/purchase/TicketBookConfirmTemplate';
import { purchaseEvent } from 'src/utils/gtag';
import { VerifiedRoot } from 'src/organisms/product-purchase/VerifiedRoot';
import { useGetReservationFlowMe } from 'src/hooks/api/reservation-flow/useGetReservationFlowMe';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { Stripe, StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { ReserveStripeElements } from 'src/components/stripe/ReserveStripeElements';
import {
  convertPaymentMethodForSubmission,
  isStripeSpecificError,
} from 'src/utils/payment';
import { SubscriptionPurchaseFormValues } from 'src/pages/[publicId]/subscriptions/[productPublicId]/purchase';
import { captureException } from '@sentry/nextjs';
import { buildSiteUrl } from 'src/utils/request-url';
import { useIsMulpayOrderEnabled } from 'src/hooks/api/reservation-flow/useIsMulpayOrderEnabled';
import { TokenResponse, TokenObj } from '@mul-pay/mptoken-js';
import { MulpayCardData } from 'src/interfaces/mullpayCardData';
import { applySecureCspHeaders } from 'src/utils/csp';
import { executeWithRetryForSpecificError } from 'src/utils/booking';

export interface TicketBookPurchaseFormValues {
  customerInfo: { [key: string]: string };
  paymentMethod:
    | ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo
    | ReservationFlowSelectedPaymentMethodNewCreditCard;
  cardToken?: string;
  confirmationToken?: string;
  mulpayCardData?: MulpayCardData;
}

type Props = {
  merchantPublicId: string;
  productPublicId: number;
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (ctx) => {
    const { query, token, session, requestId, res } = ctx;
    const merchantPublicId = String(query.publicId);
    const merchantDetailResponse = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId,
    });

    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }

    const { productPublicId } = ctx.params as { productPublicId: string };

    if (isNaN(Number(productPublicId))) {
      return {
        notFound: true,
      };
    }

    // 事前決済を含むページのため、CSPを設定する
    applySecureCspHeaders(res, { requestId: requestId });

    return {
      props: {
        merchantPublicId,
        productPublicId: Number(productPublicId),
      },
    };
  },
);

const Inner: FC<{
  merchantPublicId: string;
  productPublicId: number;
}> = ({ merchantPublicId, productPublicId }) => {
  const router = useRouter();
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();

  const [currentPage, setCurrentPage] = useState<
    'inputPaymentInfo' | 'confirm'
  >('inputPaymentInfo');

  const { reset, control, setValue, resetField, setError } = useFormContext();
  const formValues = useWatch({ control });
  const { data: user } = useGetReservationFlowMe();
  const { data: merchantDetail } = useMerchantDetail(merchantPublicId);
  const { data: productDetail, isLoading } = useGetReservationFlowProduct({
    parameter: {
      merchant_public_id: merchantPublicId,
      product_public_id: productPublicId,
    },
  });

  const isMulpayOrderEnabled = useIsMulpayOrderEnabled(merchantPublicId);

  useEffect(() => {
    if (!productDetail || isLoading) return;

    reset({
      customerInfo: productDetail.customer_fields.reduce((acc, current) => {
        if (current.default_value === null) {
          return acc;
        }

        return {
          ...acc,
          [`c_${current.id}`]: current.default_value,
        };
      }, {}),
    });
  }, [productDetail, reset, isLoading]);

  const genericErrorMessage = intl.formatMessage({
    defaultMessage: 'エラーが発生しました。',
    id: 'v1Q/QC',
    description:
      'src/pages/[publicId]/ticket_books/[productPublicId]/purchase/index.tsx',
  });
  const getSubmitErrorMessage = useCallback(
    (error: ReservationFlowInvalidRequestError) => {
      const errorDetails = filterReservationFlowInvalidRequestDetails(
        error.details,
      );

      if (errorDetails.length === 0) {
        return genericErrorMessage;
      }

      if (errorDetails[0].invalid_items.length === 0) {
        return genericErrorMessage;
      }

      const item = errorDetails[0].invalid_items[0];

      if (
        item.type === 'invalid_payment_method' ||
        item.type === 'credit_card_update_unavailable' ||
        item.type === 'unrecoverable_error'
      ) {
        return item.message;
      }

      return genericErrorMessage;
    },
    [genericErrorMessage],
  );

  const client = useClientSideApiClient();

  // Stripeの場合はオーソリ取得まで実施してtokenを取得する
  const handleTicketBookWithPaymentForStripe = async ({
    response,
    paymentMethod,
    stripe,
  }: {
    response: { stripe_client_secret?: string | null };
    paymentMethod: { confirmation_token?: string | null };
    stripe: Stripe | null;
  }) => {
    const clientSecret = response.stripe_client_secret;
    if (!clientSecret)
      throw new Error('Unexpected error: missing stripe_client_secret');
    if (!stripe) throw new Error('Stripe instance not initialized');

    const { error } = await stripe.confirmPayment({
      clientSecret,
      confirmParams: {
        confirmation_token: paymentMethod.confirmation_token ?? undefined,
      },
      redirect: 'if_required',
    });

    if (error) throw error;
  };

  const submitTicketBookWithPayment = useCallback(
    async (
      formValues: TicketBookPurchaseFormValues,
      customer_fields: { id: number; value: string }[],
    ) => {
      // GMO-PG決済かつユーザーが保存しているクレジットカードが選択されている場合にpaymentMethod作成用にMulpayCardDataを設定
      const mulpayCardData: MulpayCardData | undefined =
        formValues.paymentMethod.type === 'credit_card' &&
        isMulpayOrderEnabled &&
        user?.credit_card?.onfile_card_identifier
          ? {
              type: 'existing',
              mulpayOnfileCardIdentifier:
                user.credit_card.onfile_card_identifier,
            }
          : formValues.mulpayCardData;

      const paymentMethod = convertPaymentMethodForSubmission(
        formValues.paymentMethod,
        {
          stripeConfirmationTokenId: formValues.confirmationToken,
          productType: 'ticket_book',
          mulpayCardData,
          isMulpayOrderEnabled: isMulpayOrderEnabled,
        },
      );

      const csrfToken = await fetchCsrfToken();

      const startRes = await client.startTicketBookPurchase({
        parameter: {
          merchant_public_id: merchantPublicId,
          'X-Csrf-Token': csrfToken,
        },
        requestBody: {
          product_public_id: productPublicId,
          customer_fields,
          payment_method: paymentMethod,
        },
      });

      // GmoPgの場合は3DSecure完了後のCallbackページ(src/templates/merchants/purchase/components/Processing/index.tsx)で決済確定処理が実行される
      if (
        paymentMethod.type === 'prepayment_with_gmo_credit' ||
        paymentMethod.type === 'onfile_prepayment_with_gmo_credit'
      ) {
        if (startRes.gmo_pg_redirect_url) {
          window.location.href = startRes.gmo_pg_redirect_url;
          throw new Error(await new Promise<never>(() => {}));
        } else {
          // `gmo_pg_redirect_url`は型定義上nullが入りうるが、
          // paymentMethod.typeが上のifでチェックしている値のときは必ず値が入っている
          throw new Error('Unexpected error');
        }
      }

      // ここから先はStripe決済の場合のみ実行される
      await handleTicketBookWithPaymentForStripe({
        response: startRes,
        paymentMethod: paymentMethod,
        stripe: stripe,
      });

      // Stripeの場合は、`startTicketBookPurchase`の際にsave_as_last_usedが利用され、こちらでは参照されないので一律falseで良い
      return await executeWithRetryForSpecificError(async () => {
        return await client.notifyConfirmedTicketBookPurchase({
          parameter: {
            merchant_public_id: merchantPublicId,
            'X-Csrf-Token': csrfToken,
          },
          requestBody: {
            product_issuance_id: startRes.product_issuance_id,
            save_as_last_used: false,
          },
        });
      });
    },
    [
      client,
      isMulpayOrderEnabled,
      merchantPublicId,
      productPublicId,
      stripe,
      user?.credit_card?.onfile_card_identifier,
    ],
  );

  const onFormSubmit = useCallback<SubmitHandler<TicketBookPurchaseFormValues>>(
    async (formValues) => {
      try {
        const customer_fields = Object.entries(formValues.customerInfo)
          .filter(([id, value]) => {
            if (typeof value === 'undefined' || value.length === 0) {
              return false;
            }

            const parsedId = parseInt(id, 10);

            return !isNaN(parsedId);
          })
          .map(([id, value]) => {
            return { id: parseInt(id, 10), value };
          });

        let submitRes = null;
        if (!user) {
          captureException('Never');
          throw new Error('Never');
        } else {
          submitRes = await submitTicketBookWithPayment(
            formValues,
            customer_fields,
          );

          if (!submitRes) {
            return;
          }
        }

        purchaseEvent({
          step: 'completion',
          productPublicId: submitRes.product_public_id?.toString() ?? '',
          customerPublicId: submitRes.customer_public_id.toString(),
        });
      } catch (error) {
        let parsedError: ReservationFlowInvalidRequestError | undefined =
          undefined;
        if (isAxiosError(error) && typeof error.response !== 'undefined') {
          if (
            isAPIError(error.response.data) &&
            isReservationFlowInvalidRequestError(error.response.data)
          ) {
            parsedError = error.response.data;
          }
        }

        let message;
        if (parsedError) {
          message = getSubmitErrorMessage(parsedError);
        } else if (isStripeSpecificError(error)) {
          // Stripe から出力されるメッセージをそのまま出すのはセキュリティ上よろしくないので、こちらで定義したメッセージを出す
          message = (
            <p>
              お支払いに失敗しました。別のカードをご利用いただくか、ご利用のカード会社にお問い合わせください。
              <a
                href="https://reserve-faq.stores.jp/hc/ja/articles/47415558684441"
                target="_blank"
                rel="noreferrer noopener"
              >
                詳細はこちら
              </a>
            </p>
          );
        } else {
          message = genericErrorMessage;
        }

        toast({
          variant: 'error',
          title: intl.formatMessage({
            defaultMessage: '回数券の購入に失敗しました',
            id: 'pm94sA',
            description:
              'src/pages/[publicId]/ticket_books/[productPublicId]/purchase/index.tsx',
          }),
          description: message,
        });
        setCurrentPage('inputPaymentInfo');
        return;
      }

      await router.push(
        buildSiteUrl(
          `/products/${productPublicId}#product-available-services`,
          'next',
        ),
      );
      toast({
        variant: 'success',
        title: intl.formatMessage({
          defaultMessage: '回数券を購入しました',
          id: '3NXIX5',
          description:
            'src/pages/[publicId]/ticket_books/[productPublicId]/purchase/index.tsx',
        }),
        description: productDetail?.name,
      });
    },
    [
      genericErrorMessage,
      getSubmitErrorMessage,
      intl,
      productDetail?.name,
      productPublicId,
      router,
      submitTicketBookWithPayment,
      user,
    ],
  );

  const onNextFromPaymentInput = useCallback(async () => {
    if (!stripe || !elements) {
      return;
    }
    const paymentMethod = formValues.paymentMethod;
    if (!!paymentMethod && paymentMethod.type === 'new_credit_card') {
      if (
        isMulpayOrderEnabled &&
        typeof formValues.mulpayCardData?.mulpayMpToken === 'string' &&
        formValues.mulpayCardData?.mulpayMpToken!.length > 0
      ) {
        setCurrentPage('confirm');
        return;
      }
      try {
        const { confirmationToken, error } =
          await stripe.createConfirmationToken({
            elements,
            params: {
              payment_method_data: {
                billing_details: {
                  address: {
                    country: 'JP',
                  },
                },
              },
            },
          });
        if (error) {
          throw error;
        }
        setValue('confirmationToken', confirmationToken.id);

        setCurrentPage('confirm');
      } catch (error) {
        resetField('cardToken');
        switch ((error as stripe.StripeError).code) {
          case 'card_declined':
            setError('paymentMethod.creditCardInfo', {
              type: 'manual',
              message: intl.formatMessage({
                defaultMessage:
                  '利用不可のカード番号です。今一度ご確認ください。',
                id: '1w/9IK',
                description:
                  'src/pages/[publicId]/ticket_books/[productPublicId]/purchase/index.tsx',
              }),
            });
            break;
          case 'expired_card':
            setError('paymentMethod.creditCardInfo', {
              type: 'manual',
              message: intl.formatMessage({
                defaultMessage:
                  '有効期限切れのカード番号です。今一度ご確認ください。',
                id: 'gFMw6N',
                description:
                  'src/pages/[publicId]/ticket_books/[productPublicId]/purchase/index.tsx',
              }),
            });
            break;
          default:
            setError('paymentMethod.creditCardInfo', {
              type: 'manual',
              message: intl.formatMessage({
                defaultMessage:
                  'クレジットカード決済ができませんでした。今一度クレジットカード情報を確認してください。',
                id: 'vacoN8',
                description:
                  'src/pages/[publicId]/ticket_books/[productPublicId]/purchase/index.tsx',
              }),
            });
            break;
        }
      }
    } else if (!!paymentMethod && paymentMethod.type === 'credit_card') {
      setCurrentPage('confirm');
    } else {
      captureException('Never');
      throw new Error('Never');
    }
  }, [
    elements,
    formValues.paymentMethod,
    formValues.mulpayCardData,
    isMulpayOrderEnabled,
    intl,
    resetField,
    setError,
    setValue,
    stripe,
  ]);

  const handlePaymentElementChange = async (
    event: StripePaymentElementChangeEvent,
  ) => {
    if (!stripe || !elements || !event.complete) {
      return;
    }

    const { error: submitError } = await elements.submit();

    if (!submitError) {
      setValue(
        'paymentMethod',
        {
          type: 'new_credit_card',
          // TODO: [3ds] legacy排除後はこの形で保持しなくても良い
          creditCardInfo: {},
          isStoreCreditCard: (
            formValues.paymentMethod as ReservationFlowSelectedPaymentMethodNewCreditCard
          )?.isStoreCreditCard,
        },
        { shouldValidate: true },
      );
    } else {
      resetField('paymentMethod.creditCardInfo');
    }
  };

  const handleMulpayTokenChange = useCallback(
    (tokenResponse?: TokenResponse) => {
      if (tokenResponse?.result != 'success') {
        setValue('mulpayCardData', undefined);
        return;
      }

      const isStoreCreditCard =
        formValues.paymentMethod?.type === 'new_credit_card'
          ? formValues.paymentMethod.isStoreCreditCard
          : false;

      // TokenResponse　は　TokenObj　と　ErrorResponse　の　union　だが、discriminated union　になっておらず
      // resultの値からは型が推論されないので、　as　で強制型変換を行っている
      const tokenObj = tokenResponse as TokenObj;

      setValue(
        'paymentMethod',
        {
          type: 'new_credit_card',
          creditCardInfo: {},
          isStoreCreditCard,
        },
        { shouldValidate: true },
      );
      setValue('mulpayCardData', {
        type: 'new',
        mulpayMpToken: tokenObj.tokenList[0],
      });
    },
    [
      setValue,
      formValues.paymentMethod?.type,
      formValues.paymentMethod?.isStoreCreditCard,
    ],
  );

  if (!merchantDetail) return null;

  return (
    <>
      <MetaTag
        title={intl.formatMessage({
          defaultMessage: '回数券購入',
          id: 'HkXv1+',
          description:
            'src/pages/[publicId]/ticket_books/[productPublicId]/purchase/index.tsx',
        })}
        noIndex={true}
        googleAnalyticsFourTrackingId={
          merchantDetail.available_google_analytics_four_tracking_id
        }
        faviconImageUrl={merchantDetail?.favicon_image?.url}
        currentPath={buildSiteUrl(`/ticket_books/${productPublicId}/purchase`)}
      />
      <RSToastContainer />
      <VerifiedRoot
        merchantPublicId={merchantPublicId}
        productPublicId={productPublicId}
        pageTitle={intl.formatMessage({
          defaultMessage: '回数券購入',
          id: 'HkXv1+',
          description:
            'src/pages/[publicId]/ticket_books/[productPublicId]/purchase/index.tsx',
        })}
      >
        {productDetail && (
          <>
            {currentPage === 'inputPaymentInfo' ? (
              <InputPaymentInfoTemplate
                onNext={onNextFromPaymentInput}
                onBack={() =>
                  router.push(
                    buildSiteUrl(`/products/${productPublicId}`, 'next'),
                  )
                }
                productDetail={productDetail}
                handlePaymentElementChange={handlePaymentElementChange}
                isMulpayOrderEnabled={!!isMulpayOrderEnabled}
                handleMulpayTokenChange={handleMulpayTokenChange}
                merchantPublicId={merchantPublicId}
              />
            ) : (
              <TicketBookConfirmTemplate
                onBack={() => setCurrentPage('inputPaymentInfo')}
                onNext={onFormSubmit}
                productDetail={productDetail}
              />
            )}
          </>
        )}
      </VerifiedRoot>
    </>
  );
};

const TicketBookPurchase: NextPage<Props> = ({
  merchantPublicId,
  productPublicId,
}) => {
  const { data: merchantDetail } = useMerchantDetail(merchantPublicId);
  const { data: productDetail } = useGetReservationFlowProduct({
    parameter: {
      merchant_public_id: merchantPublicId,
      product_public_id: productPublicId,
    },
  });

  const formMethods = useForm<SubscriptionPurchaseFormValues>({
    mode: 'onBlur',
  });

  const paymentMethod = useWatch({
    control: formMethods.control,
    name: 'paymentMethod',
  });

  if (!merchantDetail) return null;

  const stripeProps = productDetail?.is_wait_first_anchor
    ? ({
        mode: 'setup',
      } as const)
    : ({
        mode: 'payment',
        savePaymentMethod:
          paymentMethod &&
          paymentMethod.type === 'new_credit_card' &&
          paymentMethod.isStoreCreditCard,
        amount: productDetail?.price,
        sellerId: merchantDetail.seller_external_id,
      } as const);

  return (
    <FormProvider {...formMethods}>
      <ReserveStripeElements {...stripeProps}>
        <Inner
          merchantPublicId={merchantPublicId}
          productPublicId={productPublicId}
        />
      </ReserveStripeElements>
    </FormProvider>
  );
};

export default TicketBookPurchase;
